// Single source of truth for whether a tenant's access is enabled, mirroring the
// DB function public.is_tenant_access_enabled. Used by useStandardRole (the gate
// state) and ProtectedRoute (the realtime kill-switch listener).
//
//  - it is explicitly inactive (is_active = false), or
//  - its subscription_status is a blocking status — EXCEPT an 'expired'
//    subscription still inside its grace window (subscription_grace_period_ends_at
//    in the future), which keeps full access, or
//  - it is on trial and the trial end date has passed (live expiry — does not wait
//    for the daily check-trial-expirations cron to flip the status).
//

const BLOCKING_STATUSES: Record<string, true> = {
  suspended: true,
  cancelled: true,
  paused: true,
  churned: true,
  expired: true,
};

export interface TenantAccessInput {
  is_active?: boolean | null;
  subscription_status?: string | null;
  trial_ends_at?: string | null;
  subscription_grace_period_ends_at?: string | null;
}

/** A subscription_status that denies app access on its own. */
export function isBlockingStatus(status: string | null | undefined): boolean {
  return !!status && BLOCKING_STATUSES[status] === true;
}

/**
 * Returns true when the tenant may access the app. A missing tenant returns true
 * (callers handle the "no tenant context" / super-admin cases separately).
 */
export function isTenantAccessEnabled(
  tenant: TenantAccessInput | null | undefined,
  now: Date = new Date()
): boolean {
  if (!tenant) return true;
  if (tenant.is_active === false) return false;

  const status = tenant.subscription_status ?? "";
  if (isBlockingStatus(status)) {
    // Grace period: a lapsed ('expired') subscription keeps full access until the
    // grace window ends — mirrors public.is_tenant_access_enabled.
    const graceEndsAt = tenant.subscription_grace_period_ends_at;
    const inGrace =
      status === "expired" &&
      !!graceEndsAt &&
      new Date(graceEndsAt).getTime() > now.getTime();
    if (!inGrace) return false;
  }

  // Live trial expiry: block as soon as the trial end date passes, even if the
  // status hasn't been flipped to 'suspended' by the cron yet.
  if (status === "trial" && tenant.trial_ends_at) {
    if (new Date(tenant.trial_ends_at).getTime() < now.getTime()) return false;
  }

  return true;
}
